<template>
  <OnBoardingFrame
    back
    :step="8"
    :title="$t('on_boarding.profile_coming_from_page.title')"
    :subtitle="$t('on_boarding.profile_coming_from_page.subtitle')"
    :error="errorMessage"
    split-footer
    @back="backTapped"
  >
    <!-- Step 8, the last one: "Where are you coming from?" — the hiking app the
         hiker used before HiiKER, or that they're new to them. iOS and Android
         ask the same question with the same stored values (users.coming_from).

         Optional on purpose, like the step before it: it's a question for us,
         not for the user, so it must never stand between someone and the app. -->
    <ul class="ob-chips sources" role="listbox" :aria-label="$t('on_boarding.profile_coming_from_page.title')">
      <li v-for="source in sources" :key="source.value">
        <button
          type="button"
          class="ob-chip source"
          :class="{ 'ob-chip--on source--selected': source.value === selected }"
          role="option"
          :aria-selected="source.value === selected"
          :data-testid="`coming-from-${source.value}`"
          @click="choose(source.value)"
        >
          <span v-if="source.value === selected" class="ms" aria-hidden="true">check</span>
          {{ source.label }}
        </button>
      </li>
    </ul>

    <template #footer>
      <button type="button" class="ob-btn ob-btn-secondary ob-skip" :disabled="isLoading" @click="skip">
        <span v-if="isLoading && pending === 'skip'" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <span>{{ $t('on_boarding.profile_coming_from_page.skip') }}</span>
      </button>
      <button type="button" class="ob-btn ob-btn-primary" :disabled="isLoading || !selected" @click="submit">
        <span v-if="isLoading && pending === 'finish'" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <span>{{ $t('on_boarding.profile_coming_from_page.finish') }}</span>
      </button>
    </template>
  </OnBoardingFrame>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingFrame from './OnBoardingFrame.vue';
import { COMING_FROM_OPTIONS, type ComingFromOption } from '../../../utils/helpers/signupProfile';

export default defineComponent({
  name: 'OnBoardingProfileComingFromPage',
  components: { OnBoardingFrame },
  data() {
    return {
      selected: this.$store.state.onBoarding.profileComingFrom as string | null,
      // Which footer button started the save, so only that one shows the spinner.
      pending: null as null | 'skip' | 'finish',
    };
  },
  computed: {
    // Every label is named by a literal $t() call rather than a key built from
    // `source.labelKey`. The client bundle is assembled by scanning these call
    // sites (lib/i18n_client_bundle.rb), and a key stitched together at runtime
    // is invisible to that scan — the strings would simply be absent from the
    // bundle and the step would render raw key names.
    sources(): Array<ComingFromOption & { label: string }> {
      const labels: Record<string, string> = {
        alltrails: this.$t('on_boarding.profile_coming_from_page.alltrails'),
        os_maps: this.$t('on_boarding.profile_coming_from_page.os_maps'),
        komoot: this.$t('on_boarding.profile_coming_from_page.komoot'),
        outdooractive: this.$t('on_boarding.profile_coming_from_page.outdooractive'),
        new_to_hiking_apps: this.$t('on_boarding.profile_coming_from_page.new_to_hiking_apps'),
      };
      return COMING_FROM_OPTIONS.map((option) => ({ ...option, label: labels[option.labelKey] }));
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
  },
  methods: {
    // Tapping the chosen option again clears it — the only way back to "no
    // answer" once something has been picked.
    choose(value: string) {
      this.selected = this.selected === value ? null : value;
    },
    submit() {
      this.pending = 'finish';
      this.$store.dispatch('onBoarding/submitProfileComingFrom', this.selected);
    },
    // An explicit null, so a picked answer is cleared rather than kept.
    skip() {
      this.pending = 'skip';
      this.selected = null;
      this.$store.dispatch('onBoarding/submitProfileComingFrom', null);
    },
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>
